/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import moment from 'moment';

// Ant Design
import {
  Row,
  Col,
  Table,
  Tag,
  Typography,
  Select,
  Input,
  Button,
  Popconfirm,
  Tooltip,
  Alert,
  message,
  Steps,
} from 'antd';

//Styles
import { detailsDivStyle } from '../styling/inline-style';
const { Title, Text } = Typography;
const { TextArea } = Input;

const AudienceDetails = (props) => {
  let {
    audienceDetails,
    record,
    handleUpdateAudience,
    handleActivateAudience,
    handlePublishAudience,
    handleCreateAudience,
    handleFetchAudienceDemographs,
    handleSaveAudiencesToApi,
  } = props;

  const { name, createdAt, createdBy, reachCount, isArchived, tags } = audienceDetails;
  const { name: createdByName } = createdBy;

  const [loading, setLoading] = useState(false);
  const [audienceState, setAudienceState] = useState({});
  const [activationInitiated, setActivationInitiated] = useState(false);

  // Clear local flag once subscription or DB flags provide real status
  useEffect(() => {
    if (record._jobStatus || record.isPushedToS3Statara) {
      setActivationInitiated(false);
    }
  }, [record._jobStatus, record.isPushedToS3Statara]);

  // Parse prePublishedMetadata once for reuse
  const parsedMeta = (() => {
    try {
      const meta = typeof record.prePublishedMetadata === 'string'
        ? JSON.parse(record.prePublishedMetadata)
        : record.prePublishedMetadata;
      // Handle legacy { set: ... } wrapper from Prisma
      return meta?.set || meta;
    } catch { return null; }
  })();

  // Check if persisted job is stale (started more than 30 minutes ago without progress updates)
  const isPersistedJobStale = (() => {
    if (!parsedMeta?.status || parsedMeta.status !== 'ACTIVE') return false;
    const startedAt = parsedMeta.startedAt ? new Date(parsedMeta.startedAt) : null;
    return startedAt && (Date.now() - startedAt.getTime() > 30 * 60 * 1000);
  })();

  const persistedActiveJob = (parsedMeta?.status === 'ACTIVE' && !isPersistedJobStale) ? parsedMeta : null;

  const isPublishingInProgress =
    activationInitiated ||
    (record._jobStatus && record._jobStatus.status === 'ACTIVE') ||
    !!persistedActiveJob;

  // Polling fallback: refetch data when publishing is in progress but subscription isn't delivering
  useEffect(() => {
    const hasLiveSubscription = !!record._jobStatus;
    if (!isPublishingInProgress || hasLiveSubscription) return;
    const interval = setInterval(() => {
      handleFetchAudienceDemographs();
    }, 10000);
    return () => clearInterval(interval);
  }, [isPublishingInProgress, record._jobStatus]);

  // Parse persisted error info from prePublishedMetadata
  const persistedError = parsedMeta?.error ? parsedMeta : null;

  // Get error info from live subscription or persisted data
  const jobError = record._jobStatus?.status === 'FAILED'
    ? { jobType: record._jobStatus.jobType, message: record._jobStatus.message }
    : persistedError
      ? { jobType: persistedError.jobType, message: persistedError.error }
      : null;

  const isSaveInProgress =
    (record._jobStatus?.jobType === 'SAVE' && record._jobStatus?.status === 'ACTIVE') ||
    (record.audienceId && !record.isPushedToS3MJ && !jobError);

  // Initialize the state with the audience details
  useEffect(() => {
    setAudienceState(audienceDetails);
  }, []);

  const getPipelineStep = (record) => {
    if (!record) return 0;
    if (record.isPublished) return 3;
    if (record.isPushedToS3Statara) return 2;
    if (record.isPushedToS3MJ) return 1;
    return 0;
  };

  const getStepStatus = (stepIndex, currentStep, jobStatus, record, jobError) => {
    const jobStepMap = { SAVE: 0, ACTIVATE: 1, PUBLISH: 2 };
    // Check live subscription FAILED status
    if (jobStatus && jobStatus.status === 'FAILED') {
      if (jobStepMap[jobStatus.jobType] === stepIndex) return 'error';
    }
    // Check persisted error
    if (jobError && !jobStatus) {
      if (jobStepMap[jobError.jobType] === stepIndex) return 'error';
    }
    if (jobStatus && jobStatus.status === 'ACTIVE') {
      if (jobStepMap[jobStatus.jobType] === stepIndex) return 'process';
    }
    // Activate step in progress based on persisted flags
    if (stepIndex === 1 && record.isPushedToS3MJ && !record.isPushedToS3Statara) return 'process';
    // Publish step in progress based on persisted flags
    if (stepIndex === 2 && record.isPushedToS3Statara && !record.isPublished) return 'process';
    if (stepIndex < currentStep) return 'finish';
    if (stepIndex === currentStep) return 'process';
    return 'wait';
  };

  const handleActivation = async (audienceDemograph) => {
    setLoading(true);
    setActivationInitiated(true);
    try {
      await handleActivateAudience(audienceDemograph);
      message.success('Audience activation started');
      await handleFetchAudienceDemographs();
    } catch (error) {
      console.error(error);
      setActivationInitiated(false);
      message.error('Failed to activate audience', 2);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (audienceDemograph) => {
    setLoading(true);
    try {
      const result = await handlePublishAudience(audienceDemograph);
      const response = result?.data?.publishDemographAudience;
      if (response && response.success === false) {
        message.error(response.message || 'Failed to publish audience', 4);
      } else {
        message.success('Audience publishing started');
      }
      await handleFetchAudienceDemographs();
    } catch (error) {
      console.error(error);
      message.error('Failed to publish audience', 2);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id, isArchived = true) => {
    try {
      setLoading(true);
      await handleUpdateAudience({ id, isArchived: isArchived });
      await handleFetchAudienceDemographs();
      message.success('Audience has been archived', 2);
      setLoading(false);
    } catch (error) {
      console.log(error);
      message.error('Failed to delete audience', 2);
    }
  };

  const handleUpdateAudienceRecord = async (data) => {
    try {
      setLoading(true);

      const cleanedData = {
        id: data.id,
        name: data.name,
        description: data.description,
        tags: { set: data.tags },
      };

      const tagList = cleanedData.tags.set;

      const hasEmptyTag = tagList.some((item) => !item || item.trim() === '');
      if (hasEmptyTag) {
        message.error('Tag cannot be empty');
        setLoading(false);
        return;
      }

      await handleUpdateAudience({ ...cleanedData });
      await handleFetchAudienceDemographs();

      setAudienceState(undefined);
      message.success('Audience has been updated', 2);
      setLoading(false);
    } catch (error) {
      console.log(error);
      message.error('Failed to update audience', 2);
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Published On',
      dataIndex: 'publishedOn',
      key: 'pubishedOn',
      render: (text) => <p>{text}</p>,
    },
    {
      title: 'DSP',
      dataIndex: 'dsp',
      key: 'dsp',
      render: (text) => <p>{text}</p>,
    },
    {
      title: 'Data CPM',
      dataIndex: 'dataCpm',
      key: 'dataCpm',
      render: (text) => <p>{text}</p>,
    },
  ];

  return (
    <>
      {/* Section A: Details */}
      <Row style={{ marginBottom: '25px' }}>
        <Title level={5}>Details</Title>
      </Row>
      <Row style={{ marginBottom: '25px' }} gutter={16}>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <Text type="secondary">Audience Name</Text>
          <br />
          {!record.isPushedToS3MJ ? (
            <Input
              value={audienceState.name}
              onChange={(e) => setAudienceState({ ...audienceState, name: e.target.value })}
            />
          ) : (
            <div style={detailsDivStyle}>{name}</div>
          )}
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <Text type="secondary">Description</Text>
          <br />
          <TextArea
            value={audienceState.description}
            onChange={(e) => {
              const newValue = e.target.value;
              setAudienceState({ ...audienceState, description: newValue });
            }}
            rows={2}
          />
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <Text type="secondary">Tags</Text>
          <br />
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Select or create tags"
            value={audienceState.tags}
            onChange={(value) => {
              setAudienceState({ ...audienceState, tags: value });
            }}
          >
            <Select.Option key="Female">Female</Select.Option>
            <Select.Option key="Bay Area">Bay Area</Select.Option>
            <Select.Option key="Walnut Creek">Walnut Creek</Select.Option>
          </Select>
        </Col>
      </Row>
      <Row style={{ marginBottom: '25px' }}>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <Text type="secondary">Reach</Text>
          <br />
          <Text>{reachCount ? reachCount.toLocaleString('en-US') : '0'}</Text>
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <Text type="secondary">Authored</Text>
          <br />
          <Text>{createdByName}</Text>
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <Text type="secondary">Created On</Text>
          <br />
          <Text>{moment(createdAt).format('MM/DD/YYYY hh:mma')}</Text>
        </Col>
      </Row>
      <Row style={{ marginBottom: '25px', marginLeft: '1px' }} gutter={8}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <Tooltip title={isPublishingInProgress ? 'You cannot update while the Publishing is still in progress' : undefined}>
              <Button
                loading={loading}
                style={{ marginRight: '0.5em' }}
                type="primary"
                disabled={isArchived || isPublishingInProgress}
                onClick={() => {
                  handleUpdateAudienceRecord(audienceState);
                }}
              >
                Update
              </Button>
            </Tooltip>

            {!isArchived && (
              <Popconfirm
                title="Are you sure you want to archive this record?"
                onConfirm={() => {
                  handleArchive(record.id);
                }}
                okText="Yes"
                cancelText="No"
                disabled={isPublishingInProgress}
              >
                <Button danger loading={loading} disabled={isArchived || isPublishingInProgress}>
                  Archive
                </Button>
              </Popconfirm>
            )}
            {isArchived && (
              <Popconfirm
                title="Are you sure you want to unarchive this record?"
                onConfirm={() => {
                  handleArchive(record.id, false);
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button danger loading={loading}>
                  Unarchive
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title="Duplicate this audience as a new Draft?"
              onConfirm={async () => {
                try {
                  setLoading(true);
                  await handleCreateAudience({
                    name: `${record.name} (Copy)`,
                    description: record.description || '',
                    reachCount: record.reachCount,
                    tags: record.tags || [],
                    metrics: record.metrics || [],
                  });
                  message.success('Audience duplicated as Draft');
                  await handleFetchAudienceDemographs();
                } catch (error) {
                  message.error('Failed to duplicate audience');
                } finally {
                  setLoading(false);
                }
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ marginLeft: 8 }} loading={loading}>
                Duplicate
              </Button>
            </Popconfirm>
        </Col>
      </Row>

      {/* Section C: Publish Pipeline */}
      <Row style={{ marginBottom: '25px' }}>
        <Col span={24}>
          <Title level={5}>Publish Pipeline</Title>
          <Steps
            size="small"
            current={getPipelineStep(record)}
            style={{ marginBottom: 16 }}
            items={[
              {
                title: 'Save',
                description: (jobError?.jobType === 'SAVE')
                  ? 'Failed'
                  : record.isPushedToS3MJ
                    ? 'Saved to S3'
                    : isSaveInProgress
                      ? 'Saving...'
                      : 'Save audience data',
                status: getStepStatus(0, getPipelineStep(record), record._jobStatus, record, jobError),
              },
              {
                title: 'Activate',
                description: (jobError?.jobType === 'ACTIVATE')
                  ? 'Failed'
                  : record.isPushedToS3Statara
                    ? 'Activated'
                    : (activationInitiated || persistedActiveJob?.jobType === 'ACTIVATE' || (record._jobStatus?.jobType === 'ACTIVATE' && record._jobStatus?.status === 'ACTIVE'))
                      ? 'Activating...'
                      : 'Activate audience',
                status: (activationInitiated || persistedActiveJob?.jobType === 'ACTIVATE') && !record._jobStatus
                  ? 'process'
                  : getStepStatus(1, getPipelineStep(record), record._jobStatus, record, jobError),
              },
              {
                title: 'Publish',
                description: (jobError?.jobType === 'PUBLISH')
                  ? 'Failed'
                  : record.isPublished
                    ? 'Published to DSP'
                    : (persistedActiveJob?.jobType === 'PUBLISH' || (record._jobStatus?.jobType === 'PUBLISH' && record._jobStatus?.status === 'ACTIVE'))
                      ? 'Publishing...'
                      : 'Publish to DSP',
                status: (persistedActiveJob?.jobType === 'PUBLISH') && !record._jobStatus
                  ? 'process'
                  : getStepStatus(2, getPipelineStep(record), record._jobStatus, record, jobError),
              },
            ]}
          />

          {jobError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${jobError.jobType} job failed`}
              description={jobError.message}
            />
          )}

          {!record.isPublished && !record.isArchived && (
            <Row>
              <Col>
                {!record.isPushedToS3MJ && (
                  <Popconfirm
                    title="Are you sure you want to save this Audience?"
                    onConfirm={() => handleSaveAudiencesToApi({
                      selectedAudiences: [record],
                      selectedDSP: 'Nexxen',
                    }).then(() => {
                      message.success('Audience save job started');
                      handleFetchAudienceDemographs();
                    }).catch(() => message.error('Failed to start save job'))}
                    okText="Yes"
                    cancelText="No"
                    disabled={isSaveInProgress}
                  >
                    <Button
                      type="primary"
                      loading={loading}
                      disabled={isSaveInProgress}
                    >
                      Save Audience
                    </Button>
                  </Popconfirm>
                )}
                {record.isPushedToS3MJ && !record.isPushedToS3Statara && (
                  <>
                    <Popconfirm
                      title="Are you sure you want to activate this Audience?"
                      onConfirm={() => handleActivation({
                        ...record,
                        id: record.id,
                        audienceId: record.s3MJDetails?.audienceId,
                      })}
                      okText="Yes"
                      cancelText="No"
                      disabled={isPublishingInProgress}
                    >
                      <Button
                        type="primary"
                        loading={loading}
                        disabled={isPublishingInProgress}
                      >
                        Activate
                      </Button>
                    </Popconfirm>
                    {isPublishingInProgress && persistedActiveJob && (
                      <Popconfirm
                        title="Reset the publishing status? Use this if the job was cancelled or cleared from the queue."
                        onConfirm={async () => {
                          try {
                            setLoading(true);
                            await handleUpdateAudience({ id: record.id, prePublishedMetadata: null });
                            await handleFetchAudienceDemographs();
                            message.success('Status reset successfully');
                          } catch (error) {
                            message.error('Failed to reset status');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Tooltip title="Reset the current job status. Use this if the job was cancelled, stuck, or cleared from the queue.">
                          <Button
                            style={{ marginLeft: 8 }}
                            loading={loading}
                          >
                            Reset Status
                          </Button>
                        </Tooltip>
                      </Popconfirm>
                    )}
                  </>
                )}
                {record.isPushedToS3Statara && !record.isPublished && (
                  <>
                    <Popconfirm
                      title="Are you sure you want to publish this Audience to DSP?"
                      onConfirm={() => handlePublish({
                        ...record,
                        id: record.id,
                        audienceId: record.s3MJDetails?.audienceId,
                      })}
                      okText="Yes"
                      cancelText="No"
                      disabled={isPublishingInProgress}
                    >
                      <Button
                        type="primary"
                        loading={loading}
                        disabled={isPublishingInProgress}
                      >
                        Publish
                      </Button>
                    </Popconfirm>
                    {isPublishingInProgress && persistedActiveJob && (
                      <Popconfirm
                        title="Reset the publishing status? Use this if the job was cancelled or cleared from the queue."
                        onConfirm={async () => {
                          try {
                            setLoading(true);
                            await handleUpdateAudience({ id: record.id, prePublishedMetadata: null });
                            await handleFetchAudienceDemographs();
                            message.success('Status reset successfully');
                          } catch (error) {
                            message.error('Failed to reset status');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Tooltip title="Reset the current job status. Use this if the job was cancelled, stuck, or cleared from the queue.">
                          <Button
                            style={{ marginLeft: 8 }}
                            loading={loading}
                          >
                            Reset Status
                          </Button>
                        </Tooltip>
                      </Popconfirm>
                    )}
                  </>
                )}
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      {/* Publishing History */}
      <Row style={{ marginBottom: '25px' }}>
        <Col span={24}>
          <Title level={5}>Publishing History</Title>
          <Table
            columns={columns}
            dataSource={
              record.publishedDate
                ? [{
                    key: '1',
                    publishedOn: moment(record.publishedDate).format('MM/DD/YYYY hh:mma'),
                    dsp: 'Nexxen',
                    dataCpm: record.postPublishedMetadata?.dataCpm || '-',
                  }]
                : []
            }
            pagination={false}
            locale={{ emptyText: 'No publishing history' }}
          />
        </Col>
      </Row>
    </>
  );
};

export default AudienceDetails;
